"use client";

import "@/components/ui/molecules/categories/feedback.css";
import { Button } from "@/components/ui/shadcn/button";
import { copyTextToClipboard } from "@/lib/browser/clipboard";
import { CheckIcon, CircleCheckIcon, CopyIcon, InfoIcon, OctagonXIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";

import { toast } from "sonner";

type ToastType = "success" | "error" | "warning" | "info";

const config: Record<ToastType, { icon: React.ElementType; }> = { success: { icon: CircleCheckIcon }, error: { icon: OctagonXIcon }, warning: { icon: TriangleAlertIcon }, info: { icon: InfoIcon } };

function toastCopyPayload(message: string, description?: string, copyText?: string): string {
    return [message, description, copyText].filter((part) => part && part.trim().length > 0).join("\n\n");
}

export const CustomToast = memo(function CustomToast(
    { id, type, message, description, duration = 4000, copyText, action, showing = 0 }: {
        id: string | number;
        /** Which showing this is: a keyed toast replaced under the same id restarts its countdown instead of inheriting
         *  the one it replaced. */
        showing?: number;
        type: ToastType;
        message: string;
        description?: string;
        duration?: number;
        copyText?: string;
        action?: { label: string; onClick: () => void };
    }
) {
    const [progress, setProgress] = useState(100);
    const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
    const { icon: Icon } = config[type];
    const persistUntilDismissed = type === "error";
    const copyPayload = useMemo(() => toastCopyPayload(message, description, copyText), [copyText, description, message]);

    const handleCopy = async () => {
        if (!copyPayload) {
            return;
        }
        const ok = await copyTextToClipboard(copyPayload);
        setCopyState(ok ? "copied" : "failed");
        setTimeout(() => setCopyState("idle"), 2000);
    };

    useEffect(() => {
        if (persistUntilDismissed) {
            return;
        }

        const startTime = Date.now();

        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
            setProgress(remaining);

            if (remaining === 0) {
                clearInterval(interval);
                toast.dismiss(id);
            }
        }, 16);

        return () => clearInterval(interval);
    }, [duration, id, persistUntilDismissed, showing]);

    return (
        <div className="dw-custom-toast" data-type={type} data-component="CustomToast" data-location="src/components/ui/molecules/CustomToast.tsx">
            <Icon className="dw-custom-toast__icon" />

            <div className="dw-custom-toast__content">
                <p className="dw-custom-toast__message">{message}</p>
                {description && <p className="dw-custom-toast__description">{description}</p>}
            </div>

            <div className="dw-custom-toast__actions">
                {action && <Button variant="ghost" size="sm" onClick={() => { toast.dismiss(id); action.onClick(); }}>{action.label}</Button>}
                <Button
                    variant="ghost"
                    size="auto"
                    onClick={() => void handleCopy()}
                    className="dw-custom-toast__copy-btn"
                    aria-label={copyState === "copied" ? "Copied" : copyState === "failed" ? "Copy failed" : "Copy toast"}>
                    {copyState === "copied"
                        ? <CheckIcon />
                        : <CopyIcon />}
                </Button>

                <Button variant="ghost" size="auto" onClick={() => toast.dismiss(id)} className="dw-custom-toast__dismiss-btn" aria-label="Dismiss">
                    <XIcon />
                </Button>
            </div>

            {/* Progress bar — errors stay until dismissed */}
            {!persistUntilDismissed
                ? (
                    <div className="dw-custom-toast__progress-track">
                        <div
                            className="dw-custom-toast__progress-bar"
                            // eslint-disable-next-line no-restricted-syntax -- dynamic: runtime-computed value
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                )
                : null}
        </div>
    );
});
